// First-person PLAYER — ported almost verbatim from zombies (entities/player.ts). Pointer-lock
// mouse-look + WASD, jump/gravity with bunny-hop, health + delayed regen. The camera IS the
// player's eyes. Collision is done EXTERNALLY (main samples the level nav's heightAt and calls
// applyGround) — exactly the extension point the zombies version left open.
//
// Adaptations for longfight: config comes from PLAYER (stud-scaled, "shrunk" to little-person size)
// instead of zombies' WORLD; sprint reads Input.isBoost(); the view-bob cadence/amplitude are scaled
// to the stud world. Perk/roguelike modifier fields are kept (the future shop writes to them).

import * as THREE from "three";
import { PLAYER } from "../config";
import type { Input } from "../engine/input";
import { sfx } from "../engine/sfx";

export class Player {
  position = new THREE.Vector3(0, PLAYER.eyeHeight, 0);
  yaw = 0; // radians, around Y
  pitch = 0; // radians, clamped
  health: number = PLAYER.maxHealth;
  alive = true;

  // --- modifiers the future shop/upgrades will write to (defaults = no upgrade) ---
  maxHealth: number = PLAYER.maxHealth;
  speedMul = 1;
  regenMul = 1;
  incomingDmgMul = 1;
  lifesteal = 0;
  jumpCount = 0;
  /** Terrain-driven acceleration multiplier (ice slicks → drift). SEPARATE from speedMul so
   *  hazards never stomp a purchased speed perk. Reset by the hazard sampler each frame. */
  envAccelMul = 1;

  // --- jumping --- (grounded is set by the external collide step)
  private vy = 0;
  grounded = true;
  sprinting = false;

  // --- velocity-based movement (accel/decel) + bhop ---
  private velX = 0;
  private velZ = 0;
  private bhop = 0; // 0..1 speed boost from chained jumps

  // --- juice: view bob, sprint FOV, recoil kick ---
  private bobPhase = 0;
  private bobAmt = 0; // eases in/out with movement
  private baseFov: number;
  private recoilPitch = 0; // transient upward kick, decays
  private recoilOffset = new THREE.Vector3();
  /** horizontal speed last frame (for bob/accel feel) */
  speed2d = 0;

  get velocityX(): number {
    return this.velX;
  }
  get velocityZ(): number {
    return this.velZ;
  }
  get velocityY(): number {
    return this.vy;
  }

  get bhopActive(): boolean {
    return this.bhop > 0.25;
  }

  private lastDamageTime = -Infinity;
  private readonly lookSensitivity = 0.0022;
  private readonly maxPitch = Math.PI / 2 - 0.05;

  constructor(private camera: THREE.PerspectiveCamera) {
    this.baseFov = PLAYER.fov; // first-person FOV (wider than the spectator camera's default)
    this.syncCamera();
  }

  /** Apply an upward recoil kick (radians) — called by the game on each shot. */
  addRecoil(pitchKick: number): void {
    this.recoilPitch = Math.min(this.recoilPitch + pitchKick, 0.14);
  }

  update(dt: number, input: Input): void {
    // --- look ---
    const look = input.consumeLook();
    this.yaw -= look.dx * this.lookSensitivity;
    this.pitch -= look.dy * this.lookSensitivity;
    this.pitch = Math.max(-this.maxPitch, Math.min(this.maxPitch, this.pitch));

    // --- move (velocity-based: accelerate toward the input direction) ---
    const axis = input.moveAxis(); // axis.z: W=-1 S=+1, axis.x: D=+1 A=-1
    const moving = axis.x !== 0 || axis.z !== 0;
    this.sprinting = moving && input.isBoost();

    const sy = Math.sin(this.yaw), cy = Math.cos(this.yaw);
    const forward = TMP_A.set(-sy, 0, -cy);
    const right = TMP_B.set(cy, 0, -sy);
    let tx = 0;
    let tz = 0;
    if (moving) {
      tx = forward.x * -axis.z + right.x * axis.x;
      tz = forward.z * -axis.z + right.z * axis.x;
      const inv = 1 / Math.hypot(tx, tz);
      tx *= inv;
      tz *= inv;
    }
    const sprintMul = this.sprinting ? PLAYER.sprintMul : 1;
    const maxSpeed = PLAYER.speed * this.speedMul * sprintMul * (1 + this.bhop * 0.6);
    const targetVx = tx * maxSpeed;
    const targetVz = tz * maxSpeed;
    // snappier on the ground, floaty in the air (bhop air-control)
    const accel = (this.grounded ? (moving ? 14 : 12) * this.envAccelMul : 3) * dt;
    const a = Math.min(1, accel);
    this.velX += (targetVx - this.velX) * a;
    this.velZ += (targetVz - this.velZ) * a;
    this.position.x += this.velX * dt;
    this.position.z += this.velZ * dt;
    this.speed2d = Math.hypot(this.velX, this.velZ);

    // --- jump / gravity + bhop ---
    if (input.isDown("Space") && this.grounded) {
      this.vy = PLAYER.jumpSpeed;
      this.grounded = false;
      this.jumpCount++;
      sfx.play("jump");
      if (this.speed2d > 1) this.bhop = Math.min(1, this.bhop + 0.22); // chain bonus
    }
    if (this.grounded) this.bhop = Math.max(0, this.bhop - dt * 1.6); // bleeds off on the ground
    this.vy -= PLAYER.gravity * dt;
    this.position.y += this.vy * dt;

    // --- view bob (cadence + amplitude scaled to the stud world) ---
    const bobTarget = this.grounded && this.speed2d > 1 ? 1 : 0;
    this.bobAmt += (bobTarget - this.bobAmt) * Math.min(1, dt * 8);
    this.bobPhase += (this.speed2d / PLAYER.speed) * dt * 9;

    // --- sprint / bhop FOV ---
    const fovTarget = this.baseFov + (this.sprinting ? 10 : 0) + this.bhop * 8;
    if (Math.abs(this.camera.fov - fovTarget) > 0.05) {
      this.camera.fov += (fovTarget - this.camera.fov) * Math.min(1, dt * 8);
      this.camera.updateProjectionMatrix();
    }

    // --- recoil decay ---
    this.recoilPitch *= Math.max(0, 1 - dt * 9);

    // --- regen ---
    if (this.alive && this.health < this.maxHealth) {
      const since = (performance.now() - this.lastDamageTime) / 1000;
      if (since >= PLAYER.regenDelay) {
        this.health = Math.min(this.maxHealth, this.health + PLAYER.regenRate * this.regenMul * dt);
      }
    }

    this.syncCamera();
  }

  damage(amount: number): void {
    if (!this.alive) return;
    this.health -= amount * this.incomingDmgMul;
    this.lastDamageTime = performance.now();
    if (this.health <= 0) {
      this.health = 0;
      this.alive = false;
    }
  }

  /** Direction the camera is actually looking (includes pitch) — for shooting. */
  lookDirection(out: THREE.Vector3): THREE.Vector3 {
    return out
      .set(
        -Math.sin(this.yaw) * Math.cos(this.pitch),
        Math.sin(this.pitch),
        -Math.cos(this.yaw) * Math.cos(this.pitch),
      )
      .normalize();
  }

  /** Called by the game after the external collide step resolves the position. */
  applyGround(grounded: boolean): void {
    this.grounded = grounded;
    if (grounded && this.vy < 0) this.vy = 0;
    this.syncCamera();
  }

  syncCamera(): void {
    const off = this.recoilOffset.copy(this.position);
    if (this.bobAmt > 0.01) {
      const amp = PLAYER.eyeHeight;
      const by = Math.sin(this.bobPhase * 2) * amp * 0.035 * this.bobAmt;
      const bx = Math.cos(this.bobPhase) * amp * 0.025 * this.bobAmt;
      off.y += by;
      off.x += Math.cos(this.yaw) * bx;
      off.z += -Math.sin(this.yaw) * bx;
    }
    this.camera.position.copy(off);
    this.camera.rotation.order = "YXZ";
    this.camera.rotation.y = this.yaw;
    this.camera.rotation.x = this.pitch + this.recoilPitch;
  }

  /** Reposition + heal for a (re)spawn, KEEPING upgrade modifiers (maxHealth/speedMul/regenMul/…).
   *  Used when entering play and on death-respawn, so shop perks persist. */
  softRespawn(spawn: THREE.Vector3, yaw = 0): void {
    this.position.copy(spawn);
    this.yaw = yaw;
    this.pitch = 0;
    this.health = this.maxHealth;
    this.alive = true;
    this.vy = 0;
    this.velX = 0;
    this.velZ = 0;
    this.bhop = 0;
    this.grounded = true;
    this.sprinting = false;
    this.recoilPitch = 0;
    this.bobAmt = 0;
    this.camera.fov = this.baseFov;
    this.camera.updateProjectionMatrix();
    this.lastDamageTime = -Infinity;
    this.syncCamera();
  }

  reset(spawn?: THREE.Vector3): void {
    this.position.copy(spawn ?? new THREE.Vector3(0, PLAYER.eyeHeight, 0));
    this.yaw = 0;
    this.pitch = 0;
    this.maxHealth = PLAYER.maxHealth;
    this.speedMul = 1;
    this.regenMul = 1;
    this.incomingDmgMul = 1;
    this.lifesteal = 0;
    this.health = this.maxHealth;
    this.alive = true;
    this.vy = 0;
    this.velX = 0;
    this.velZ = 0;
    this.bhop = 0;
    this.grounded = true;
    this.sprinting = false;
    this.recoilPitch = 0;
    this.bobAmt = 0;
    this.camera.fov = this.baseFov;
    this.camera.updateProjectionMatrix();
    this.lastDamageTime = -Infinity;
    this.syncCamera();
  }
}

const TMP_A = new THREE.Vector3();
const TMP_B = new THREE.Vector3();
